import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import {
  parseGscDateRangeFromUrl,
  resolveGscDateRange,
} from "@/lib/date-utils";
import {
  gscService,
} from "@/services/GscService";
import {
  GSC_DEFAULT_COMPARE_METRIC,
  GSC_DEFAULT_PAGE_SIZE,
  GSC_MAX_COMPARE_DOMAINS,
  EMPTY_GSC_DOMAIN_COMPARE,
  EMPTY_GSC_DOMAIN_OVERVIEW_RESULT,
  isGscCompareMetric,
  isGscDetailSortColumn,
  isGscDomainOverviewSortColumn,
  isGscPageSize,
  isGscTrendGranularity,
  parseGscActiveFiltersFromUrl,
  type GscCompareMetric,
  type GscDateRangeOption,
  type GscDetailResult,
  type GscDetailSortColumn,
  type GscDomainCompareResponse,
  type GscDomainOverviewResult,
  type GscDomainOverviewSortColumn,
  type GscFilterOptions,
  type GscKpiSummary,
  type GscPageSize,
  type GscSortDirection,
  type GscTrendGranularity,
  type GscTrendPoint,
  type UrlActiveFilters,
} from "@/types/gsc";

export const GSC_STATS_STALE_TIME = 1000 * 60 * 5;
export const GSC_FILTER_OPTIONS_STALE_TIME = 1000 * 60 * 30;

export const GSC_FILTER_OPTIONS_QUERY_KEY = ["gsc", "filterOptions"] as const;

export const EMPTY_GSC_DETAIL_RESULT: GscDetailResult = {
  rows: [],
  totalCount: 0,
};

export { EMPTY_GSC_DOMAIN_OVERVIEW_RESULT };

const parseCommaSeparated = (param: string | null): string[] =>
  param ? param.split(",").map((value) => value.trim()).filter(Boolean) : [];

const parsePageParam = (param: string | null): number => {
  const page = Number(param ?? "1");
  return Number.isNaN(page) || page < 1 ? 1 : page;
};

/** Compare page domains — repeated `?domain=a&domain=b` (always an array). */
export const parseGscCompareDomains = (
  searchParams: URLSearchParams,
): string[] => {
  const fromRepeated = searchParams.getAll("domain");
  const domains =
    Array.isArray(fromRepeated) && fromRepeated.some((value) => value.trim())
      ? fromRepeated
      : parseCommaSeparated(searchParams.get("domains"));

  if (!Array.isArray(domains)) return [];

  return domains
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, GSC_MAX_COMPARE_DOMAINS);
};

export const parseGscCompareMetric = (
  searchParams: URLSearchParams,
): GscCompareMetric => {
  const raw = searchParams.get("metric");
  if (raw && isGscCompareMetric(raw)) return raw;
  return GSC_DEFAULT_COMPARE_METRIC;
};

const parseTrendDomainsParam = (param: string | null): string[] | null => {
  const domains = parseCommaSeparated(param);
  return domains.length > 0 ? domains : null;
};

export interface GscSharedParams {
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
  activeFilters: UrlActiveFilters;
}

export type GscKpiParams = GscSharedParams;

export interface GscDomainOverviewParams extends GscSharedParams {
  page: number;
  pageSize: GscPageSize;
  sortBy: GscDomainOverviewSortColumn;
  sortDir: GscSortDirection;
}

export interface GscTrendParams extends GscSharedParams {
  granularity: GscTrendGranularity;
  trendDomains: string[] | null;
}

export interface GscDetailParams extends GscSharedParams {
  page: number;
  pageSize: GscPageSize;
  sortBy: GscDetailSortColumn;
  sortDir: GscSortDirection;
}

export interface GscCompareParams {
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
  compareEnabled: boolean;
  domains: string[];
  metric: GscCompareMetric;
}

export const parseGscSharedParamsFromUrl = (url: URL): GscSharedParams => ({
  ...parseGscDateRangeFromUrl(url.searchParams),
  activeFilters: parseGscActiveFiltersFromUrl(url),
});

export const parseGscKpiParamsFromUrl = (url: URL): GscKpiParams =>
  parseGscSharedParamsFromUrl(url);

export const parseGscDomainOverviewParamsFromUrl = (
  url: URL,
): GscDomainOverviewParams => {
  const shared = parseGscSharedParamsFromUrl(url);
  const rawPageSize = Number(
    url.searchParams.get("pageSize") ?? GSC_DEFAULT_PAGE_SIZE,
  );
  const rawSortBy = url.searchParams.get("sortBy") ?? "clicks";

  return {
    ...shared,
    page: parsePageParam(url.searchParams.get("ovPage")),
    pageSize: isGscPageSize(rawPageSize) ? rawPageSize : GSC_DEFAULT_PAGE_SIZE,
    sortBy: isGscDomainOverviewSortColumn(rawSortBy) ? rawSortBy : "clicks",
    sortDir: url.searchParams.get("sortDir") === "asc" ? "asc" : "desc",
  };
};

export const parseGscTrendParamsFromUrl = (url: URL): GscTrendParams => {
  const shared = parseGscSharedParamsFromUrl(url);
  const rawGranularity = url.searchParams.get("granularity") ?? "day";

  return {
    ...shared,
    granularity: isGscTrendGranularity(rawGranularity) ? rawGranularity : "day",
    trendDomains: parseTrendDomainsParam(url.searchParams.get("domains")),
  };
};

export const parseGscDetailParamsFromUrl = (url: URL): GscDetailParams => {
  const shared = parseGscSharedParamsFromUrl(url);
  const rawSortBy = url.searchParams.get("sortBy") ?? "clicks";
  const rawPageSize = Number(
    url.searchParams.get("pageSize") ?? GSC_DEFAULT_PAGE_SIZE,
  );

  return {
    ...shared,
    page: parsePageParam(url.searchParams.get("page")),
    pageSize: isGscPageSize(rawPageSize) ? rawPageSize : GSC_DEFAULT_PAGE_SIZE,
    sortBy: isGscDetailSortColumn(rawSortBy) ? rawSortBy : "clicks",
    sortDir: url.searchParams.get("sortDir") === "asc" ? "asc" : "desc",
  };
};

export const parseGscCompareParamsFromUrl = (url: URL): GscCompareParams => ({
  ...parseGscDateRangeFromUrl(url.searchParams),
  compareEnabled: url.searchParams.get("compare") === "true",
  domains: parseGscCompareDomains(url.searchParams),
  metric: parseGscCompareMetric(url.searchParams),
});

export const getGscKpiQueryKey = (params: GscKpiParams) =>
  ["gsc", "kpi", params] as const;

export const getGscDomainOverviewQueryKey = (params: GscDomainOverviewParams) =>
  ["gsc", "domainOverview", params] as const;

export const getGscTrendQueryKey = (params: GscTrendParams) =>
  ["gsc", "trend", params] as const;

export const getGscDetailQueryKey = (params: GscDetailParams) =>
  ["gsc", "detail", params] as const;

/** Metric is UI-only — omit from the compare RPC cache key. */
export const getGscCompareQueryKey = (params: GscCompareParams) =>
  [
    "gsc",
    "compare",
    {
      dateRange: params.dateRange,
      customFrom: params.customFrom,
      customTo: params.customTo,
      compareEnabled: params.compareEnabled,
      domains: params.domains,
    },
  ] as const;

export const fetchGscFilterOptions = (): Promise<GscFilterOptions> =>
  gscService.getFilterOptions();

export const fetchGscKpi = (params: GscKpiParams): Promise<GscKpiSummary> =>
  gscService.getKpiSummary(params);

export const fetchGscDomainOverview = (
  params: GscDomainOverviewParams,
): Promise<GscDomainOverviewResult> => gscService.getDomainOverview(params);

export const fetchGscTrend = (params: GscTrendParams): Promise<GscTrendPoint[]> =>
  gscService.getTrend(params);

export const fetchGscDetail = (params: GscDetailParams): Promise<GscDetailResult> =>
  gscService.getDetail(params);

export const fetchGscCompare = (
  params: GscCompareParams,
): Promise<GscDomainCompareResponse> => gscService.getDomainCompare(params);

export const useGscFilterOptions = () =>
  useQuery({
    queryKey: GSC_FILTER_OPTIONS_QUERY_KEY,
    queryFn: fetchGscFilterOptions,
    staleTime: GSC_FILTER_OPTIONS_STALE_TIME,
    // Filter options only fill the dropdowns — a failure (e.g. the country scan
    // timing out) must not throw to the route boundary and 500 the whole page.
    // The page falls back to empty options + a note. (Global default is
    // throwOnError: true.)
    throwOnError: false,
  });

export const useGscKpi = (params: GscKpiParams) =>
  useQuery({
    queryKey: getGscKpiQueryKey(params),
    queryFn: () => fetchGscKpi(params),
    placeholderData: keepPreviousData,
    staleTime: GSC_STATS_STALE_TIME,
    // Soft-fail to the page's hasLoadError alert instead of a route-boundary 500
    // (global default is throwOnError: true).
    throwOnError: false,
  });

export const useGscDomainOverview = (
  params: GscDomainOverviewParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: getGscDomainOverviewQueryKey(params),
    queryFn: () => fetchGscDomainOverview(params),
    placeholderData: keepPreviousData,
    staleTime: GSC_STATS_STALE_TIME,
    enabled: options?.enabled ?? true,
    throwOnError: false, // soft-fail to hasLoadError, not a 500
  });

export const useGscTrend = (
  params: GscTrendParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: getGscTrendQueryKey(params),
    queryFn: () => fetchGscTrend(params),
    placeholderData: keepPreviousData,
    staleTime: GSC_STATS_STALE_TIME,
    enabled: options?.enabled ?? true,
    throwOnError: false, // soft-fail to hasLoadError, not a 500
  });

export const useGscDetail = (params: GscDetailParams) =>
  useQuery({
    queryKey: getGscDetailQueryKey(params),
    queryFn: () => fetchGscDetail(params),
    placeholderData: keepPreviousData,
    staleTime: GSC_STATS_STALE_TIME,
    /** Local Alert in GscDetail — do not bubble to the route error boundary. */
    throwOnError: false,
  });

export const useGscCompare = (params: GscCompareParams) => {
  const domains = Array.isArray(params.domains) ? params.domains : [];
  const query = useQuery({
    queryKey: getGscCompareQueryKey({ ...params, domains }),
    queryFn: () => fetchGscCompare({ ...params, domains }),
    enabled: domains.length > 0,
    placeholderData: keepPreviousData,
    staleTime: GSC_STATS_STALE_TIME,
  });

  return {
    ...query,
    data: query.data ?? EMPTY_GSC_DOMAIN_COMPARE,
  };
};

export type GscDateRangeControlsVariant = "overview" | "detail" | "compare";

export const getGscLayoutVariant = (
  pathname: string,
): GscDateRangeControlsVariant => {
  if (pathname.startsWith("/gsc/detail")) return "detail";
  if (pathname.startsWith("/gsc/compare")) return "compare";
  return "overview";
};

/** Freshness timestamp for the active GSC screen (reads cached query state). */
export const useGscDataUpdatedAt = (): number | undefined => {
  const { pathname, search } = useLocation();
  const url = useMemo(() => new URL(`https://local?${search}`), [search]);
  const variant = getGscLayoutVariant(pathname);

  const kpiParams = parseGscKpiParamsFromUrl(url);
  const detailParams = parseGscDetailParamsFromUrl(url);
  const compareParams = parseGscCompareParamsFromUrl(url);

  const kpiQuery = useQuery({
    queryKey: getGscKpiQueryKey(kpiParams),
    queryFn: () => fetchGscKpi(kpiParams),
    enabled: variant === "overview",
    staleTime: GSC_STATS_STALE_TIME,
  });

  const detailQuery = useQuery({
    queryKey: getGscDetailQueryKey(detailParams),
    queryFn: () => fetchGscDetail(detailParams),
    enabled: variant === "detail",
    staleTime: GSC_STATS_STALE_TIME,
    throwOnError: false,
  });

  const compareQuery = useQuery({
    queryKey: getGscCompareQueryKey(compareParams),
    queryFn: () => fetchGscCompare(compareParams),
    enabled: variant === "compare" && compareParams.domains.length > 0,
    staleTime: GSC_STATS_STALE_TIME,
  });

  if (variant === "detail") return detailQuery.dataUpdatedAt;
  if (variant === "compare") return compareQuery.dataUpdatedAt;
  return kpiQuery.dataUpdatedAt;
};

/** Resolved yyyy-MM-dd range for loaders / prefetch (mirrors service date logic). */
export const resolveGscParamsDateRange = (params: {
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
}) => resolveGscDateRange(params.dateRange, params.customFrom, params.customTo);
